import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Card,
    Checkbox,
    DatePicker,
    DropdownFilter,
    Heading,
    Icon,
    Loader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderRowGroup,
    TableRow,
    Tooltip,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { GetOrganizationDataErrorStatsResponse } from '@common/interfaces/api/organizationDataErrorsApi';
import { OrganizationDataErrorType, OrganizationKgoStatus, OrganizationType } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';

type OrganizationChecksStatsType =
    | 'unverified'
    | 'totalAgbErrors'
    | 'totalDuplicateErrors'
    | 'verified'
    | 'attentionRequired'
    | 'inProgress'
    | 'missingAgb'
    | 'invalidAgb'
    | 'expiredAgb'
    | 'duplicateAddress'
    | 'duplicateAgbCode'
    | 'duplicateKvkNumber'
    | 'duplicateNameStatutory'
    | 'active'
    | 'archived';

type OrganizationChecksStatsFilters = {
    date?: Date;
    organizationTypes: OrganizationType[];
};

export default function OrganizationChecksStats() {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const [stats, setStats] = useState<GetOrganizationDataErrorStatsResponse>();
    const [isLoading, setIsLoading] = useState(false);
    const [organizationChecksStatsFilters, setOrganizationChecksStatsFilters] =
        useState<OrganizationChecksStatsFilters>({ organizationTypes: [] });
    const keys: OrganizationChecksStatsType[] = [
        'unverified',
        OrganizationKgoStatus.attentionRequired,
        OrganizationKgoStatus.inProgress,
        OrganizationKgoStatus.verified,
        OrganizationDataErrorType.missingAgb,
        OrganizationDataErrorType.invalidAgb,
        OrganizationDataErrorType.expiredAgb,
        'totalAgbErrors',
        OrganizationDataErrorType.duplicateAddress,
        OrganizationDataErrorType.duplicateAgbCode,
        OrganizationDataErrorType.duplicateKvkNumber,
        OrganizationDataErrorType.duplicateNameStatutory,
        'totalDuplicateErrors',
        'active',
        'archived',
    ];
    const totalKeys: OrganizationChecksStatsType[] = ['totalAgbErrors', 'totalDuplicateErrors'];

    useEffect(() => {
        loadStats();
    }, [organizationChecksStatsFilters]);

    return (
        <PageTemplate>
            {isLoading ? (
                <Box
                    alignItems="center"
                    display="flex"
                    flex={1}
                    justifyContent="center"
                    minHeight="300px"
                >
                    <Loader size="small" />
                </Box>
            ) : (
                <>
                    <Box
                        display="flex"
                        gap={[2, 4]}
                        mb={4}
                    >
                        <Box maxWidth={'300px'}>
                            <Heading size="xsmall">{translate.common.filterOnSingular}:</Heading>

                            <DropdownFilter
                                count={organizationChecksStatsFilters.organizationTypes?.length}
                                label={translate.common.organizationType}
                                onReset={() => {
                                    setOrganizationChecksStatsFilters({
                                        ...organizationChecksStatsFilters,
                                        organizationTypes: [],
                                    });
                                }}
                                width="full"
                            >
                                {renderTypesDropdown()}
                            </DropdownFilter>
                        </Box>
                        <Box>
                            <Heading size="xsmall">{translate.common.lastLoginFrom}:</Heading>

                            <DatePicker
                                onChange={(value) =>
                                    handleChangeSearchFilter('date', value ? DateTime.from(value).toDate() : undefined)
                                }
                                value={organizationChecksStatsFilters.date ?? null}
                            />
                        </Box>
                    </Box>

                    <Card sx={{ maxWidth: '800px' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{translate.common.statistic}</TableHeader>
                                        <TableHeader sx={{ textAlign: 'right' }}>{translate.common.amount}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {renderHeaderRow(
                                        'cleaningStatus',
                                        translate.organization.dataErrorStats.cleaningStatus
                                    )}
                                    {keys.map((key) => (
                                        <>
                                            {key === OrganizationDataErrorType.missingAgb &&
                                                renderHeaderRow(
                                                    key,
                                                    translate.organization.dataErrorStats.agbCodeErrorTitle
                                                )}
                                            {key === OrganizationDataErrorType.duplicateAddress &&
                                                renderHeaderRow(
                                                    key,
                                                    translate.organization.dataErrorStats.duplicateOrganizationTitle
                                                )}
                                            {key === 'active' &&
                                                renderHeaderRow(
                                                    key,
                                                    translate.organization.dataErrorStats.totalOrganizations
                                                )}
                                            <TableRow key={key}>
                                                <TableCell fontWeight={totalKeys.includes(key) ? 'medium' : 'normal'}>
                                                    {translate.organization.dataErrorStats[key]}
                                                    <Tooltip
                                                        content={translate.organization.dataErrorStats.tooltips[key]}
                                                    >
                                                        <Icon
                                                            icon="information"
                                                            sx={{ ml: 2 }}
                                                        />
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell
                                                    fontWeight={totalKeys.includes(key) ? 'medium' : 'normal'}
                                                    sx={{ textAlign: 'right' }}
                                                >
                                                    {stats?.[key] ?? 0}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </>
            )}
        </PageTemplate>
    );

    function renderHeaderRow(key: string, title: string) {
        return (
            <TableRow key={`${key}-header`}>
                <TableHeaderRowGroup colSpan={2}>
                    <Heading size="medium">{title}</Heading>
                </TableHeaderRowGroup>
            </TableRow>
        );
    }

    async function loadStats() {
        if (organizationChecksStatsFilters.date && organizationChecksStatsFilters.date.getFullYear() < 1000) {
            return;
        }

        setIsLoading(true);
        const response = await abortableFetch.fetchJson<GetOrganizationDataErrorStatsResponse>(
            '/api/organizations/dataErrors/stats',
            'POST',
            organizationChecksStatsFilters
        );

        if (response?.json) {
            setStats(response.json);
        }

        setIsLoading(false);
    }

    function renderTypesDropdown() {
        return Object.values(OrganizationType)
            .sort((a, b) => translate.organizationTypeEnum[a].localeCompare(translate.organizationTypeEnum[b]))
            .map((type) => (
                <Box
                    display="flex"
                    key={type}
                    pb={2}
                >
                    <Checkbox
                        checked={organizationChecksStatsFilters.organizationTypes.includes(type)}
                        key={type}
                        label={translate.organizationTypeEnum[type]}
                        onChange={(event) => {
                            const updatedTypeFilters = [...organizationChecksStatsFilters.organizationTypes];
                            if (event.target.checked) {
                                updatedTypeFilters.push(type);
                            } else {
                                const index = updatedTypeFilters.indexOf(type);
                                updatedTypeFilters.splice(index, 1);
                            }
                            handleChangeSearchFilter('organizationTypes', updatedTypeFilters);
                        }}
                    />
                </Box>
            ));
    }

    function handleChangeSearchFilter<K extends keyof OrganizationChecksStatsFilters>(
        name: K,
        value: OrganizationChecksStatsFilters[K]
    ) {
        setOrganizationChecksStatsFilters((previous) => ({ ...previous, [name]: value }));
    }
}
